import { SEO } from "../components/SEO";
import { PageHero } from "../components/PageHero";
import { Section, SectionHeader } from "../components/Section";
import { CTA } from "../components/CTA";
import { CTASection } from "../components/CTASection";
import { useTranslation } from "../lib/i18n";

function CheckIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: "0.125rem" }}>
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

const VENDOR_TIERS = [
  { number: 1, name: "Basic Integration License", description: "For simple API connections and workflow triggers." },
  { number: 2, name: "Advanced Integration License", description: "For deeper diagnostic, intervention, and verification integration." },
  { number: 3, name: "Automation Integration License", description: "For vendors using Sentinel's automation engine." },
  { number: 4, name: "OEM Architecture License", description: "For manufacturers embedding Sentinel into physical products." },
  { number: 5, name: "Full Vendor Certification License", description: "For vendors seeking Sentinel Certified Vendor status." },
];

export default function VendorIntegrationPage() {
  const { t } = useTranslation();
  return (
    <>
      <SEO
        title="Vendor Integration Licensing — Sentinel"
        description="Sentinel Vendor Integration Licensing allows technology vendors, software companies, automation platforms, robotics manufacturers, IoT providers, and OEMs to legally integrate Sentinel's patented architecture into their products."
        canonical="/vendor-integration"
      />
      <PageHero
        eyebrow={t("vendor.eyebrow")}
        title={t("vendor.title")}
        description={t("vendor.description")}
        bgVariant="steel"
      />

      {/* What It Allows */}
      <Section>
        <SectionHeader
          eyebrow={t("vendor.whatAllowsEyebrow")}
          title={t("vendor.whatAllowsTitle")}
        />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: "1.25rem" }}>
          {[
            { title: t("vendor.allow1.title"), desc: t("vendor.allow1.desc") },
            { title: t("vendor.allow2.title"), desc: t("vendor.allow2.desc") },
            { title: t("vendor.allow3.title"), desc: t("vendor.allow3.desc") },
            { title: t("vendor.allow4.title"), desc: t("vendor.allow4.desc") },
            { title: t("vendor.allow5.title"), desc: t("vendor.allow5.desc") },
            { title: t("vendor.allow6.title"), desc: t("vendor.allow6.desc") },
          ].map((item) => (
            <div key={item.title} className="card" style={{ padding: "1.5rem" }}>
              <h3 style={{ fontSize: "1rem", marginBottom: "0.5rem", display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <span style={{ color: "var(--accent-600)" }}><CheckIcon size={18} /></span>
                {item.title}
              </h3>
              <p className="text-muted" style={{ fontSize: "0.8125rem", lineHeight: 1.5, margin: 0 }}>{item.desc}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Who Needs It */}
      <Section variant="subtle">
        <SectionHeader
          eyebrow={t("vendor.whoNeedsEyebrow")}
          title={t("vendor.whoNeedsTitle")}
          description={t("vendor.whoNeedsDescription")}
        />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: "0.75rem" }}>
          {[
            t("vendor.who1"),
            t("vendor.who2"),
            t("vendor.who3"),
            t("vendor.who4"),
            t("vendor.who5"),
            t("vendor.who6"),
            t("vendor.who7"),
          ].map((item) => (
            <div key={item} className="card" style={{ padding: "1rem", display: "flex", alignItems: "flex-start", gap: "0.75rem" }}>
              <span style={{ color: "var(--accent-600)", flexShrink: 0 }}><CheckIcon size={16} /></span>
              <span style={{ fontSize: "0.875rem", lineHeight: 1.5 }}>{item}</span>
            </div>
          ))}
        </div>
      </Section>

      {/* What It Does Not Allow */}
      <Section>
        <SectionHeader
          eyebrow={t("vendor.restrictionsEyebrow")}
          title={t("vendor.restrictionsTitle")}
          description={t("vendor.restrictionsDescription")}
        />
        <div className="card" style={{ padding: "1.5rem" }}>
          <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: "0.625rem" }}>
            {[
              t("vendor.restrict1"),
              t("vendor.restrict2"),
              t("vendor.restrict3"),
              t("vendor.restrict4"),
              t("vendor.restrict5"),
            ].map((item) => (
              <li key={item} style={{ display: "flex", alignItems: "flex-start", gap: "0.5rem", fontSize: "0.875rem", lineHeight: 1.5 }}>
                <span style={{ color: "var(--error-500)", flexShrink: 0, fontWeight: 700 }}>{"\u2717"}</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </Section>

      {/* Vendor Licensing Tiers */}
      <Section variant="subtle">
        <SectionHeader
          eyebrow={t("vendor.tiersEyebrow")}
          title={t("vendor.tiersTitle")}
          description={t("vendor.tiersDescription")}
        />
        <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
          {VENDOR_TIERS.map((tier) => (
            <div key={tier.number} className="card" style={{ padding: "1.25rem", display: "flex", alignItems: "flex-start", gap: "1rem" }}>
              <div style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: "2rem",
                height: "2rem",
                borderRadius: "var(--radius-md)",
                background: "var(--primary-50, #eff6ff)",
                color: "var(--primary-600, #2563eb)",
                fontSize: "0.875rem",
                fontWeight: 700,
                flexShrink: 0,
              }}>
                {tier.number}
              </div>
              <div>
                <h3 style={{ fontSize: "1rem", marginBottom: "0.25rem" }}>{t(`vendor.tier${tier.number}`)}</h3>
                <p className="text-muted" style={{ fontSize: "0.8125rem", lineHeight: 1.5, margin: 0 }}>{t(`vendor.tier${tier.number}.desc`)}</p>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* How to Understand */}
      <Section>
        <SectionHeader
          eyebrow={t("vendor.howEyebrow")}
          title={t("vendor.howTitle")}
          description={t("vendor.howDescription")}
        />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: "0.75rem" }}>
          {[
            { name: "Dolby", desc: "Licensing for audio" },
            { name: "ARM", desc: "Licensing for processors" },
            { name: "Mobileye", desc: "Licensing for ADAS" },
            { name: "ISO", desc: "Licensing for compliance frameworks" },
          ].map((item) => (
            <div key={item.name} className="card" style={{ padding: "1.25rem", textAlign: "center" }}>
              <h4 style={{ fontSize: "1rem", marginBottom: "0.25rem" }}>{item.name}</h4>
              <p className="text-muted" style={{ fontSize: "0.8125rem", margin: 0 }}>{item.desc}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Why It Matters */}
      <Section variant="subtle">
        <SectionHeader
          eyebrow={t("vendor.whyEyebrow")}
          title={t("vendor.whyTitle")}
          description={t("vendor.whyDescription")}
        />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: "0.75rem" }}>
          {[
            t("vendor.why1"),
            t("vendor.why2"),
            t("vendor.why3"),
            t("vendor.why4"),
            t("vendor.why5"),
            t("vendor.why6"),
          ].map((item) => (
            <div key={item} className="card" style={{ padding: "1rem", display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <span style={{ color: "var(--accent-600)", flexShrink: 0 }}><CheckIcon size={16} /></span>
              <span style={{ fontSize: "0.875rem" }}>{item}</span>
            </div>
          ))}
        </div>
      </Section>

      <CTASection
        title={t("vendor.ctaTitle")}
        description={t("vendor.ctaDescription")}
        primaryLabel={t("vendor.ctaPrimary")}
        primaryTo="/licensing/quote?tier=vendor_integration"
        secondaryLabel={t("vendor.ctaSecondary")}
        secondaryTo="/licensing"
      />
    </>
  );
}
